import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { IconButton, styled } from "@mui/material";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { DescriptionText } from "../uiControllers/descriptionText";
import { PatientColorMap } from "../../layout/patientColorMap";
import { Maybe, QualifierDescription } from "fe-common/graphql";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { ReactComponent as RoundInfoIcon } from "../../assets/infoIconSmall.svg";
import clsx from "clsx";
import { useClinicData } from "../../clinic/clinicHooks";
import { FirstTimeShowTooltip } from "../uiControllers/firstTimeShowTooltip";

//region [[ Styles ]]
export const ShowInfoIcon = styled(IconButton)({
  "&.show-info": {
    padding: "2px",
    margin: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    height: "23.3px",
    width: "23.3px",
    ...commonBoxShadowStyle,
    transition: "all 0.5s ease-out",
    fontSize: "9px",
    background: "transparent",
    "& path": {
      fill: PatientColorMap.Primary,
    },
    "& circle": {
      stroke: PatientColorMap.Primary,
    },
    "& span": {
      width: "fit-content",
    },
    "& svg": {
      transition: "all 0.5s ease-out",
    },
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

type PartialFeature = {
  patientDescription?: Maybe<string>;

  description?: Maybe<QualifierDescription>;

  name?: string;

  shortname?: Maybe<string>;
};

export interface QuestionDescriptionProps {
  qualifiedFeature?: PartialFeature;
  questionText: string;
  isAnswered?: boolean;
  featureId?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const NO_DESCRIPTION_VALUE = "N/A";

export const QuestionDescription = React.memo(
  ({
    qualifiedFeature,
    questionText,
    isAnswered,
    featureId,
    ...props
  }: QuestionDescriptionProps) => {
    const { name: clinicName, clinic: clinicId } = useClinicData();
    const [showDescription, setShowDescription] = useState(!isAnswered);

    const description = qualifiedFeature?.patientDescription;

    const toggleDescription = useCallback(() => {
      setShowDescription((old) => !old);

      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_SHOW_DESCRIPTION,
        props: {
          clinicName,
          clinicId,
          originalText: `${questionText}-${qualifiedFeature.shortname}`,
          featureId,
        },
      });
    }, [
      setShowDescription,
      questionText,
      qualifiedFeature,
      featureId,
      clinicId,
      clinicName,
    ]);

    useEffect(() => {
      setShowDescription(false);
    }, [isAnswered]);

    const isDescriptionValid =
      description && description !== NO_DESCRIPTION_VALUE && description !== "";

    if (!isDescriptionValid) return null;

    return (
      <>
        {/*<Fade in mountOnEnter unmountOnExit>*/}
        <FirstTimeShowTooltip
          orientation={"TOP"}
          tooltipKey={"INFO_BUTTON_TOOLTIP_TEXT"}
        >
          <ShowInfoIcon
            onClick={toggleDescription}
            className={clsx(
              "show-info",
              showDescription ? "active" : "inactive",
            )}
          >
            <RoundInfoIcon />
          </ShowInfoIcon>
        </FirstTimeShowTooltip>
        {/*</Fade>*/}
        <DescriptionText
          descriptionText={description}
          onCloseDescription={toggleDescription}
          show={showDescription}
        />
      </>
    );
  },
);
